import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ApolloNamespace } from '@shared/enums/apollo-namespaces';
import { GraphQlService } from '@shared/services/graphql-service';
import { catchError, map, of, switchMap } from 'rxjs';
import {
  GET_AGENT_REGISTRATION,
  LIST_AGENTS_BY_COMPANY,
} from '../graphql/shipping-agent-management.graphql';
import {
  GetAgentRegistrationActions,
  ListAgentsByCompanyActions,
} from './shipping-agent-management.actions';
import {
  GetAgentRegistrationResponse,
  ListAgentsByCompanyResponse,
} from '../responses/shipping-agent-management.response';

@Injectable({ providedIn: 'root' })
export class ShippingAgentManagementEffects {
  private readonly actions$ = inject(Actions);
  private readonly graphQlService = inject(GraphQlService);

  private buildFailure(message?: string) {
    return {
      success: false,
      statusCode: 400,
      message: message ?? 'Network or server error',
      data: null as any,
    };
  }

  readonly loadMyApplications$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ListAgentsByCompanyActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<ListAgentsByCompanyResponse>({
            query: LIST_AGENTS_BY_COMPANY,
            apolloNamespace: ApolloNamespace.registration,
            variables: {
              pageableParam: request?.pageableParam ?? null,
              companyUuid: request?.companyUuid ?? null,
            },
          })
          .pipe(
            map((result) => {
              const payload = result.data?.listAgentsByCompany;
              if (!payload) {
                return ListAgentsByCompanyActions.failure({
                  error: this.buildFailure('No data returned'),
                });
              }
              return ListAgentsByCompanyActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: 'Applications loaded successfully',
                  data: payload,
                },
              });
            }),
            catchError((error) =>
              of(
                ListAgentsByCompanyActions.failure({
                  error: this.buildFailure(error?.message),
                }),
              ),
            ),
          ),
      ),
    ),
  );

  readonly getAgentRegistration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(GetAgentRegistrationActions.request),
      switchMap(({ request }) =>
        this.graphQlService
          .query<GetAgentRegistrationResponse>({
            query: GET_AGENT_REGISTRATION,
            apolloNamespace: ApolloNamespace.registration,
            variables: { agentUuid: request },
          })
          .pipe(
            map((result) => {
              const res = result.data?.getAgentRegistration;
              if (!res || !res.data) {
                return GetAgentRegistrationActions.failure({
                  error: this.buildFailure(res?.message ?? 'No data found'),
                });
              }
              return GetAgentRegistrationActions.success({
                response: {
                  success: true,
                  statusCode: 200,
                  message: res.message ?? 'Details loaded successfully',
                  data: res.data,
                },
              });
            }),
            catchError((error) =>
              of(
                GetAgentRegistrationActions.failure({
                  error: this.buildFailure(error?.message),
                }),
              ),
            ),
          ),
      ),
    ),
  );
}
